import {
  IRootStore,
  IUserStore,
  IUserData,
  SaveUserScoreResponse,
  IUserScoreInfo,
} from "@shared/types";
import { makeAutoObservable, toJS, runInAction } from "mobx";
import { loginWithGoogle, getUserData, saveUserScore, updateUserRank, updateUserProfile } from "../../services/api";
import { IScoreRequest, IUpdateUserRequest } from "@shared/types";
import { hebrewLettersToNumber } from "../../utils/text-hebrew.utils";
import i18n from "../../utils/i18n";

/**
 * 👤 USER CONTROLLER - Controller for managing user data
 * 
 * **Main Role:** Controller that manages all operations related to the current user
 * 
 * **Responsibilities:**
 * - User authentication and identification (Google OAuth)
 * - Managing user data (personal details, statistics)
 * - Saving reading results and calculating scores
 * - Updating user rank
 * - Managing voice precision settings
 * 
 * **Communicates with:**
 * - userService (API) - for all server operations
 * - booksStore - to get information about current selections for saving results
 * - UI Components - for displaying user data
 * 
 * **Pattern:** Repository + Controller - manages data + API calls + business logic
 */
class UserController implements IUserStore {
  rootStore: IRootStore;
  voicePrecisionLevel = 30;
  userData: IUserData | null = null;
  error: string | null = null;
  isLoading = false;

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    
    // עדכון שם משתמש אורח כשהשפה משתנה
    i18n.on('languageChanged', () => {
      this.updateGuestUserName();
    });
  }
  
  // עדכון שם משתמש אורח לפי השפה הנוכחית
  private updateGuestUserName = (): void => {
    const GUEST_USER_ID = 544;
    if (this.userData?.personalInfo?.id === GUEST_USER_ID && this.userData.personalInfo) {
      this.userData.personalInfo.name = i18n.language === 'he' ? 'משתמש/ת אורח/ת' : 'Guest User';
    }
  };

  // Simple check that the data exists
  private isValidResponse = (userStatistics: IUserScoreInfo[]): userStatistics is IUserScoreInfo[] => {
    return Array.isArray(userStatistics) && userStatistics.length > 0;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  setVoicePrecisionLevel = (voicePrecisionLevel: number) => {
    this.voicePrecisionLevel = voicePrecisionLevel;
  };

  loginWithGoogle = async (googleAccessToken: string): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await loginWithGoogle(googleAccessToken);
      // Handle login success, e.g., save token or update user data in store
    } catch (error) {
      console.error("Error logging in with Google:", error);
      this.setError("An error occurred during login. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  };

  getUserData = async (): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const data = await getUserData();
      runInAction(() => {
        this.userData = data.userData;
        
        // למשתמש אורח: טעינת rank מ-sessionStorage ותרגום השם
        const GUEST_USER_ID = 544;
        if (this.userData?.personalInfo?.id === GUEST_USER_ID) {
          const guestRank = this.getGuestRankFromStorage();
          if (this.userData.personalInfo) {
            this.userData.personalInfo.rank = guestRank;
            // תרגום השם לפי השפה הנוכחית
            this.userData.personalInfo.name = i18n.language === 'he' ? 'משתמש/ת אורח/ת' : 'Guest User';
          }
          console.log('👤 Guest user - loaded rank from sessionStorage:', guestRank);
        }
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
      this.setError("An error occurred while loading user data. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  };

  /**
   * אחרי כמה קריאות מציעים לאורח להירשם.
   * הראשונה כי הרגע ראה ציון, השלישית כי כבר נכנס לקצב, והשביעית כי בשלב
   * הזה הוא באמת בונה משהו שחבל לאבד. אחרי זה מפסיקים לשאול.
   */
  private static readonly GUEST_PROMPT_AT_READING = [1, 3, 7];

  /**
   * הספירה יושבת ב-sessionStorage יחד עם ה-rank של האורח, ולכן היא נמחקת
   * בסוף הביקור בדיוק כמו שאר הנתונים שלו.
   */
  private countGuestReadingAndMaybePrompt = (): void => {
    try {
      const stored = Number(sessionStorage.getItem('guest_reading_count') ?? '0');
      const count = (Number.isFinite(stored) ? stored : 0) + 1;
      sessionStorage.setItem('guest_reading_count', count.toString());

      if (!UserController.GUEST_PROMPT_AT_READING.includes(count)) return;

      const uiStore = this.rootStore.stores?.uiStore;
      uiStore?.setIsGuestSignInPromptPending(true);
    } catch (error) {
      // דפדפן שחוסם אחסון לא אמור לשבור קריאה
      console.warn('לא ניתן לספור קריאות של אורח:', error);
    }
  };

  // Helper functions for guest user rank storage
  private getGuestRankFromStorage = (): number => {
    const stored = sessionStorage.getItem('guest_rank');
    return stored ? parseInt(stored, 10) : 0;
  };

  private saveGuestRankToStorage = (rank: number): void => {
    sessionStorage.setItem('guest_rank', rank.toString());
  };

  /**
   * A level as the score route wants it. `Number(undefined)` is NaN, which
   * JSON writes as null, which the route reads as "no id given" and quietly
   * falls back to matching on the Hebrew name. Leave the id out instead, so a
   * missing one is visible rather than a silent downgrade.
   */
  private scoreLevel = (level?: { id?: unknown; label?: string } | null, name_he?: string) => {
    const id = Number(level?.id);
    return {
      ...(Number.isFinite(id) ? { id } : {}),
      name_he: name_he ?? level?.label,
    };
  };

  /** Siddur_Ashkenaz in the Books table. */
  private static readonly SIDDUR_BOOK_ID = 2;

  /**
   * Rank, send, and fold the answer back in. Shared by both score kinds:
   * a Siddur unit reaches here without going through the level fields.
   */
  private sendScore = async (scoreInfo: IScoreRequest, isGuestUser: boolean): Promise<void> => {
    try {
        // חישוב userRank - rank קיים + מספר מילים שנקראו נכון
        // למשתמש אורח: rank נשמר ב-sessionStorage
        const currentRank = isGuestUser 
          ? this.getGuestRankFromStorage()
          : (this.userData?.personalInfo?.rank || 0);
      
        // גישה ל-readingSessionStore דרך rootStore כדי לקבל את מספר המילים הנכונות
        const readingSessionStore = this.rootStore.stores?.readingSessionStore;
        const correctWords = scoreInfo.rankWords ?? (readingSessionStore?.stats?.correctWords || 0);
        scoreInfo.userRank = currentRank + correctWords;
      
        console.log(`📊 חישוב userRank: ${currentRank} + ${correctWords} מילים נכונות = ${scoreInfo.userRank}`);
        if (isGuestUser) {
          console.log('👤 משתמש אורח - rank נשמר ב-sessionStorage');
        }

        // Debug log the complete scoreInfo before sending
        console.log('💾 Complete scoreInfo before sending:', {
          book: scoreInfo.book,
          category: scoreInfo.category,
          subCategory: scoreInfo.subCategory,
          topSection: scoreInfo.topSection,
          section: scoreInfo.section,
          subSection: scoreInfo.subSection,
          score: scoreInfo.score,
          precision: scoreInfo.precision,
          userRank: scoreInfo.userRank
        });

        const response: SaveUserScoreResponse = await saveUserScore(scoreInfo);
      
        // למשתמש אורח: לא מעדכן scoreInfo, רק שומר rank ב-sessionStorage
        if (isGuestUser) {
          // שמירת rank ב-sessionStorage למשתמש אורח
          this.saveGuestRankToStorage(scoreInfo.userRank);
        
          runInAction(() => {
            if (this.userData?.personalInfo) {
              // עדכון rank ב-UI בלבד (לא במסד הנתונים)
              this.userData.personalInfo.rank = scoreInfo.userRank;
            }
          });
          console.log('✅ Guest user rank saved to sessionStorage:', scoreInfo.userRank);
          console.log('ℹ️ Guest user - scoreInfo not updated (tracking only)');

          this.countGuestReadingAndMaybePrompt();
        } else if (response.message === "Score saved successfully" && this.isValidResponse(response.userStatistics)) {
          // למשתמש רגיל: עדכון scoreInfo ו-rank כרגיל
          runInAction(() => {
            if (this.userData) {
              this.userData.scoreInfo = response.userStatistics;
              // עדכון rank ב-UI - השרת כבר עדכן אותו במסד הנתונים
              if (this.userData.personalInfo) {
                this.userData.personalInfo.rank = scoreInfo.userRank;
              }
            } else {
              this.userData = {
                personalInfo: null,
                scoreInfo: response.userStatistics,
              };
            }
            console.log('✅ Statistics updated:', response.userStatistics.map(b => b.book_name_en));
            console.log('✅ Rank updated in UI:', scoreInfo.userRank);
          });
        }
    } catch (error) {
      console.error("Error saving user score:", error);
      this.setError("An error occurred while saving results. Please try again later.");
    }
  };

  saveUserScore = async (requestBody: IScoreRequest): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      
      const GUEST_USER_ID = 544;
      const isGuestUser = this.userData?.personalInfo?.id === GUEST_USER_ID;
      
      const booksStore = this.rootStore.stores?.booksStore;
      const scoreInfo: IScoreRequest = { ...requestBody };

      scoreInfo.precision = this.voicePrecisionLevel;

      // A Siddur unit already knows where it is. The level fields below read
      // from booksStore, which the new reader never touches, so filling them
      // in would describe wherever the student last was in a book.
      if (scoreInfo.siddurNodeId) {
        scoreInfo.book = { name_en: 'Siddur_Ashkenaz', name_he: 'סידור אשכנז', id: UserController.SIDDUR_BOOK_ID };
        return await this.sendScore(scoreInfo, isGuestUser);
      }

      scoreInfo.book = {
        name_en: booksStore.selectedBook?.name_en || booksStore.selectedBook?.name || '',
        name_he: booksStore.selectedBook?.name_he || '',
        id: booksStore.selectedBook?.id
      };

      switch (scoreInfo.book.name_en || scoreInfo.book.name_he) {
        case "Siddur_Ashkenaz":
        case "סידור אשכנז":
          // Add Siddur score info - hierarchical structure: gate->part->topic->prayer/blessing->sub-section
          console.log('💾 Siddur score saving - selectedLevelFour:', booksStore.selectedLevelFour);
          console.log('💾 Siddur score saving - selectedLevelFive:', booksStore.selectedLevelFive);
          
          // Gate (Category) - Level 1
          scoreInfo.category = this.scoreLevel(booksStore.selectedLevelOne);

          // Part (Sub Category) - Level 2
          scoreInfo.subCategory = this.scoreLevel(booksStore.selectedLevelTwo);

          // Topic (Top Section) - Level 3
          scoreInfo.topSection = this.scoreLevel(booksStore.selectedLevelThree);

          // Prayer/Blessing - Level 4
          scoreInfo.section = this.scoreLevel(booksStore.selectedLevelFour);

          // Sub-section - Level 5, when the prayer has one
          if (booksStore.selectedLevelFive?.id) {
            scoreInfo.subSection = this.scoreLevel(booksStore.selectedLevelFive);
          }
          
          console.log('💾 Final scoreInfo.section:', scoreInfo.section);
          console.log('💾 Final scoreInfo.subSection:', scoreInfo.subSection);
          break;
        case "Mishna":
        case "משנה סדורה":
          // Mishna doesn't have a category level in the client interface
          scoreInfo.category = null;
          scoreInfo.subCategory = {
            id: Number(booksStore.selectedLevelOne?.id),
            name_he: booksStore.selectedLevelOne?.label.replace("משנה", "מסכת"),
          };
          scoreInfo.topSection = {
            id: Number(booksStore.selectedLevelTwo?.id),
            name_he: booksStore.selectedLevelTwo?.label || "",
          };
          scoreInfo.section = {
            id: Number(booksStore.selectedLevelThree?.id),
            index: hebrewLettersToNumber(
              booksStore.selectedLevelThree?.label?.replace("משנה ", "") || ""
            ),
          };
          
          console.log('💾 Mishna score saving - category:', scoreInfo.category);
          console.log('💾 Mishna score saving - subCategory:', scoreInfo.subCategory);
          console.log('💾 Mishna score saving - topSection:', scoreInfo.topSection);
          console.log('💾 Mishna score saving - section:', scoreInfo.section);
          break;
        case "Torah":
        case "תורה":
          // Torah navigates חומש → פרק → פסוק, so the parasha is no longer a
          // selector level. It still has to be scored, so read it off the
          // bookmark carried on the selected chapter (level two).
          console.log("booksStore.selectedLevelTwo: ", toJS(booksStore.selectedLevelTwo));
          scoreInfo.category = {
            id: Number(booksStore.selectedLevelOne?.id),
            name_he: booksStore.selectedLevelOne?.label
          };
          // Resolved from chapter + verse, not from the chapter alone: a
          // straddling chapter belongs to two parshiot, and scoring must
          // record the one the verse actually falls in.
          scoreInfo.subCategory = {
            id: Number(booksStore.currentParasha?.id),
            name_he: booksStore.currentParasha?.name,
          };
          scoreInfo.topSection = {
            id: Number(booksStore.selectedLevelTwo?.id),
            name_he: booksStore.selectedLevelTwo?.label,
          };
          scoreInfo.section = {
            id: Number(booksStore.selectedLevelThree?.id),
            name_he: booksStore.selectedLevelThree?.label
          };
          break;
        case "Neviim":
        case "נביאים":
          console.log("booksStore.selectedLevelThree: ", toJS(booksStore.selectedLevelThree));
          scoreInfo.category = { 
            id: Number(booksStore.selectedLevelOne?.id),
            name_he: booksStore.selectedLevelOne?.label 
          };
          scoreInfo.subCategory = {
            id: Number(booksStore.selectedLevelTwo?.id),
            name_he: booksStore.selectedLevelTwo?.label,
          };
          scoreInfo.topSection = {
            id: Number(booksStore.selectedLevelThree?.id),
            name_he: booksStore.selectedLevelThree?.label,
          };
          scoreInfo.section = { 
            id: Number(booksStore.selectedLevelFour?.id),
            name_he: booksStore.selectedLevelFour?.label
          };
          break;
        case "Ketuvim":
        case "כתובים":
          console.log("booksStore.selectedLevelThree: ", toJS(booksStore.selectedLevelThree));
          scoreInfo.category = { 
            id: Number(booksStore.selectedLevelOne?.id),
            name_he: booksStore.selectedLevelOne?.label 
          };
          scoreInfo.subCategory = {
            id: Number(booksStore.selectedLevelTwo?.id),
            name_he: booksStore.selectedLevelTwo?.label,
          };
          scoreInfo.topSection = {
            id: Number(booksStore.selectedLevelThree?.id),
            name_he: booksStore.selectedLevelThree?.label,
          };
          scoreInfo.section = { 
            id: Number(booksStore.selectedLevelFour?.id),
            name_he: booksStore.selectedLevelFour?.label
          };
          break;
        default:
          break;
      }

      await this.sendScore(scoreInfo, isGuestUser);
    } catch (error) {
      console.error("Error saving user score:", error);
      this.setError("An error occurred while saving results. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  };

  updateUserRank = async (rank: number): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      
      const GUEST_USER_ID = 544;
      const isGuestUser = this.userData?.personalInfo?.id === GUEST_USER_ID;
      
      // למשתמש אורח: שמירה ב-sessionStorage בלבד
      if (isGuestUser) {
        this.saveGuestRankToStorage(rank);
        runInAction(() => {
          if (this.userData?.personalInfo) {
            this.userData.personalInfo.rank = rank;
          }
        });
        console.log("Guest user rank updated in sessionStorage:", rank);
        this.setIsLoading(false);
        return;
      }
      
      // למשתמש רגיל: שליחה לשרת
      const response = await updateUserRank(rank);
      console.log("Response: ", response);
      if (response) {
        console.log("User rank updated successfully");
        runInAction(() => {
          this.userData!.personalInfo!.rank = response.userRank;
        });
      } else {
        console.error("Failed to update user rank");
        this.setError(
          "Could not update user rank. Please try again later."
        );
      }
    } catch (error) {
      console.error("Error updating user rank:", error);
      this.setError("An error occurred while updating user rank. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  }

  updateUserProfile = async (params: { name?: string; readingStatus?: string; avatarId?: number }): Promise<void> => {
    try {
      this.setIsLoading(true);
      this.setError(null);
      const response = await updateUserProfile(params);
      console.log("Response: ", response);
    } catch (error) {
      console.error("Error updating user profile:", error);
      this.setError("An error occurred while updating user profile. Please try again later.");
    } finally {
      this.setIsLoading(false);
    }
  } 
}

export default UserController; 